import React from "react";

import { Loader2 } from "lucide-react";

import {
  CARD_SURFACE,
  ColumnHeading,
  MoveList,
  PointList,
  QuoteBand,
  ScoreStrip,
  Section,
} from "./Chrome";
import {
  buildKpiSituationCard,
  type XFactorKpiCategoryDetail,
} from "../lib/deriveKpi";

/**
 * Account Health Situation Card — KPI Metrics Briefing counterpart to
 * SituationCard.tsx (Business Health / Public Data). Same one-screen shape:
 * score strip, why-these-scores narrative, meeting one-liner quote, Must Know /
 * Watch-outs / Recommended actions, Outlook, Evidence.
 *
 * Field mapping lives in deriveKpi.buildKpiSituationCard. Do not change
 * SituationCard.tsx / buildSituationCard — that is Business Health only.
 */
const KpiSituationCard: React.FC<{
  accountName: string;
  data?: XFactorKpiCategoryDetail;
  /** While true, show a spinner — do not treat null data as "no card yet". */
  loading?: boolean;
}> = ({ accountName, data, loading = false }) => {
  const card = React.useMemo(
    () => buildKpiSituationCard(accountName, data),
    [accountName, data]
  );

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Loading Situation Card…
      </div>
    );
  }

  if (!card.present) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        <span className="font-medium">
          This account has no KPI Situation Card yet.
        </span>{" "}
        xfactor writes the card once a KPI Metrics Briefing has been generated
        for this account and measurement date.
      </div>
    );
  }

  return (
    <div>
      <ScoreStrip tiles={card.tiles} />

      {card.narrative ? (
        <div
          className={`${CARD_SURFACE} mt-4 flex items-start justify-between gap-3 px-3 py-2.5`}
        >
          <p className="text-sm leading-relaxed text-gray-700">
            {card.narrative}
          </p>
        </div>
      ) : null}

      {card.quote ? (
        <div className="mt-4">
          <QuoteBand>{card.quote}</QuoteBand>
        </div>
      ) : null}

      <div className="mt-6 grid gap-5 lg:grid-cols-3">
        <div>
          <ColumnHeading tone="text-gray-500">Must know</ColumnHeading>
          <PointList points={card.mustKnow} />
        </div>
        <div>
          <ColumnHeading tone="text-red-500">Watch-outs</ColumnHeading>
          <PointList points={card.watchOuts} />
        </div>
        <div>
          <ColumnHeading tone="text-gray-500">
            Recommended actions and owners
          </ColumnHeading>
          <MoveList moves={card.moves} />
        </div>
      </div>

      {card.outlook ? (
        <Section title="Outlook">
          <p className="text-sm leading-relaxed text-gray-700">
            {card.outlook}
          </p>
        </Section>
      ) : null}

      {card.evidence.length ? (
        <Section title={`Evidence (${card.evidence.length})`}>
          <PointList points={card.evidence} dense hideChip />
        </Section>
      ) : null}
    </div>
  );
};

export default KpiSituationCard;
